import React, { useState, Fragment, useRef, useEffect } from "react";
import { styled } from '@mui/material/styles';
import clsx from "clsx";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Box,
  Toolbar,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";

import { gql, useQuery } from "@apollo/client";
// import MUIDate from '../HOC/MUI/MUIDate';
import moment from "moment";
import * as gqlb from "gql-query-builder";
import MUITablePagination from "../HOC/MUI/TablePagination/MUITablePagination";
import { useTranslation } from "react-i18next";
import FullScreenLoading from "../HOC/FunctionComponents/LoadingPages/FullScreenLoading";
import { useForm } from "react-hook-form";
import { CustomAutocomplete } from "../HOC/MUI/CustomAutocomplete";
import FormButton from "../CustomComponents/Buttons/FormButton";
import MUIDateRangeCustom from "../HOC/MUI/MUIDateRangeCustom";
import { Globals } from "../HOC/Classes/Globals";
import { setValidationError } from "../HOC/CustomFunctions/setValidationError";
import ListBranches from "../HOC/ComponentWithSpecificQuery/ListBranches";
import { LIST_GL_ACCOUNTS_DROPDOWN } from "../../GlobalsQuery/ListDropdown/ListDropdown";
import { pushUrl, windowUrl } from "../HOC/CustomFunctions/pushUrl";
import { useHistory } from "react-router";
import { urlParameters } from "../HOC/CustomFunctions/urlParameters";
import CellLink from "../HOC/CustomComponents/CellLink";
import { RiUserUnfollowLine } from "react-icons/ri";
import { FixedTableCell } from "../HOC/CustomComponents/FixedTableCell";
import TableFixedHeaderWraper from "../HOC/CustomComponents/TableWithFixedHeader";
import useLocalCurrency from "../../Hooks/LocalCurrency";
import { Print } from "@mui/icons-material";
import ExportAccountStatement from "./ExportAccountStatement";

const PREFIX = 'FinanceStatement';

const classes = {
  divider: `${PREFIX}-divider`,
  ingButton: `${PREFIX}-ingButton`,
  mainSearch: `${PREFIX}-mainSearch`,
  table: `${PREFIX}-table`,
  toolbar: `${PREFIX}-toolbar`,
  tableContainer: `${PREFIX}-tableContainer`,
  balanceFont: `${PREFIX}-balanceFont`,
  fieldsContainer: `${PREFIX}-fieldsContainer`,
  balance: `${PREFIX}-balance`,
  headerToolbar: `${PREFIX}-headerToolbar`,
  errorBox: `${PREFIX}-errorBox`,
  marginTop: `${PREFIX}-marginTop`,
  errorIcon: `${PREFIX}-errorIcon`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.divider}`]: {
    minWidth: "93%",
  },

  [`& .${classes.ingButton}`]: {
    margin: theme.spacing(2, 1, 2),
    height: "3.5em",
    padding: "0 30px",
    width: "100%",
  },

  [`& .${classes.mainSearch}`]: {
    margin: theme.spacing(0),
    width: "100%",
  },

  [`& .${classes.table}`]: {
    display: "grid",
  },

  [`& .${classes.toolbar}`]: {
    padding: theme.spacing(1.5, 0),
    borderBottom: "1px solid #ccd1d6",
    backgroundColor: "#f5f7f9",
  },

  [`& .${classes.tableContainer}`]: {
    position: "relative",
    borderRadius: 0,
    boxShadow: "none",
  },

  [`& .${classes.balanceFont}`]: {
    "& .MuiTableCell-body": {
      fontWeight: 600,
      fontSize: "1rem",
    },
  },

  [`& .${classes.fieldsContainer}`]: {
    padding: theme.spacing(1),
    textAlign: "center",
  },

  [`& .${classes.balance}`]: {
    textAlign: "center",
  },

  [`& .${classes.headerToolbar}`]: {
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  [`& .${classes.errorBox}`]: {
    transform: "translate(-50%, -50%)",
    top: "50%",
    left: "50%",
    position: "absolute",
    textAlign: "center",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.errorIcon}`]: {
    fontSize: "7rem",
    color: theme.palette.error.main,
  }
}));

//*********Table Function*********

export const FINANCE = gqlb.query([
  {
    operation: "glAccount",
    fields: [
      {
        operation: "startBalance:balance",
        fields: [],
        variables: {
          startBalanceDate: { name: "date", type: "Date", required: true },
        },
      },
      {
        operation: "endBalance:balance",
        fields: [],
        variables: {
          endBalanceDate: { name: "date", type: "Date", required: true },
          endDayEdge: {
            name: "dayEdge",
            type: "DayStartEnd",
          },
        },
      },
    ],
    variables: {
      glAccountId: {
        name: "id",
        type: "Int",
        required: true,
      },
    },
  },

  {
    operation: "sumJournalEntryRecords",
    fields: ["credit", "debit"],
    variables: {
      input: {
        type: "ListJournalEntryRecordsInput",
      },
    },
  },
  {
    operation: "listJournalEntryRecords",
    fields: [
      {
        paginatorInfo: ["total"],
      },
      {
        data: [
          "id",
          "debit",
          "credit",
          "description",
          { entry: ["id", "date", "code"] },
        ],
      },
    ],
    variables: {
      input: {
        type: "ListJournalEntryRecordsInput",
      },
      first: {
        type: "Int",
      },
      page: {
        type: "Int",
      },
    },
  },
]);

const initFromDate = moment(new Date())
  .locale("en")
  .subtract(1, "month")
  .add(1, "day")
  .format("YYYY-MM-DD");
const initToDate = moment(new Date()).locale("en").format("YYYY-MM-DD");

const FinanceStatement = (props) => {
  const initial = useRef(0);
  const totalDebit = useRef(0);
  const totalCredit = useRef(0);

  const { t } = useTranslation();

  // const theme = useTheme();
  // const dir = theme.direction

  const urlQuery = urlParameters(window.location.search);

  const initDateRange = [
    urlQuery["startDate"] ? urlQuery["startDate"] : initFromDate,
    urlQuery["endDate"] ? urlQuery["endDate"] : initToDate,
  ];
  const [dateRange, setDateRange] = useState(initDateRange);
  const [autocompleteValues, setAutocompleteValues] = useState({
    glAccount: null,
  });
  const [tableData, setTableData] = useState([]);
  const [search, setSearch] = useState();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  const noGlAccount =
    Globals?.user?.account && !Globals?.user?.account?.glAccount?.id;

  const userGlAccountId =
    Globals?.user?.account?.glAccount?.id ?? search?.["glAccountId"];

  const glAccountListPermission = Globals.user.hasPermission(
    "accounting.gl_account.list"
  );
  const startDate = moment(dateRange[0]).locale("en").format("YYYY-MM-DD");
  const endDate = moment(dateRange[1]).locale("en").format("YYYY-MM-DD");
  useEffect(() => {
    if (!glAccountListPermission) {
      setSearch({
        fromDate: startDate,
        startDate: startDate,
        toDate: endDate,
        endDate: endDate,
        renderState: true,
      });
    }
    if (props.match?.params?.id) {
      setSearch({
        fromDate: startDate,
        startDate: startDate,
        toDate: endDate,
        endDate: endDate,
        renderState: true,
        glAccountId: parseInt(props.match.params.id),
      });
    }
    return () => { };
  }, []);

  const {
    formState: { errors },
    handleSubmit,
    control,
    setError,
    watch,
    setValue,
  } = useForm();
  const history = useHistory();
  // const pushUrlSearch = (param, pathname, render) => {
  //   const queryParams = [];
  //   for (const i in param) {
  //     encodeURIComponent(param[i]) &&
  //       queryParams.push(
  //         encodeURIComponent(i) + "=" + encodeURIComponent(param[i])
  //       );
  //   }
  //   const queryString = queryParams.join("&");

  //   const url = history.createHref({
  //     pathname,
  //     search: "?" + queryString,
  //   });
  //   render ? pushUrl(props, url) : windowUrl(url);
  //   //this will not effect on history.location.search
  // };

  const searchParams = (param) => {
    const queryParams = [];
    for (const i in param) {
      encodeURIComponent(param[i]) &&
        queryParams.push(
          encodeURIComponent(i) + "=" + encodeURIComponent(param[i])
        );
    }
    const queryString = queryParams.join("&");
    return queryString;
  };

  const pushUrlSearch = (param, pathname, render) => {
    const params = searchParams(param);
    const url = history.createHref({
      pathname,
      search: "?" + params,
    });
    render ? pushUrl(props, url) : windowUrl(url);
  };

  const { currency } = useLocalCurrency();

  const { data, loading } = useQuery(
    gql`
      ${FINANCE.query}
    `,
    {
      variables: {
        first: rowsPerPage,
        page: page + 1,
        input: {
          fromDate: search?.["fromDate"],
          toDate: search?.["toDate"],
          ...(glAccountListPermission && {
            glAccountId: search?.["glAccountId"],
          }),
          approved: true,
        },
        glAccountId: userGlAccountId,
        startBalanceDate: search?.["startDate"],
        endBalanceDate: search?.["endDate"],

        endDayEdge: "END_OF_DAY",
      },
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
      notifyOnNetworkStatusChange: !search?.["renderState"],
      skip: !search || !userGlAccountId,
      onError: ({ graphQLErrors }) => {
        console.log(graphQLErrors);
        setValidationError(graphQLErrors, setError);
      },
      onCompleted: (data) => {
        if (initial.current === 0)
          initial.current = data?.glAccount?.startBalance;

        const records = data?.listJournalEntryRecords?.data.map((row) => {
          const journal = { ...row };
          totalCredit.current = totalCredit.current + journal.credit;
          totalDebit.current = totalDebit.current + journal.debit;
          const balance = journal.credit - journal.debit + initial.current;
          if (journal.credit !== 0) {
            initial.current = journal.credit + initial.current;
          } else if (journal.debit !== 0) {
            initial.current = -journal.debit + initial.current;
          } else if (balance === 0) {
            initial.current = 0;
          }
          if (balance <= 0) {
            journal["totalDebit"] = Math.abs(balance);
            journal["totalCredit"] = 0;
          } else if (balance >= 0) {
            journal["totalCredit"] = Math.abs(balance);
            journal["totalDebit"] = 0;
          }
          return journal;
        });
        let allRecords = [...tableData, ...records].reduce((acc, current) => {
          const x = acc.find((item) => item.id === current.id);
          if (!x) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, []);

        setTableData(allRecords);
      },
    }
  );
  const parseData = (data) => {
    return data;
  };
  const handelAutocompleteDefaultValue = (data, id, fieldName) => {
    const defaultValue = data[Object.keys(data)[0]].find(
      (i) => i.id === parseInt(id)
    );
    defaultValue &&
      setAutocompleteValues((prev) => ({
        ...prev,
        [fieldName]: defaultValue,
      }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    totalCredit.current = 0;
    totalDebit.current = 0;
    initial.current = 0;

    setTableData([]);
  };
  const onSubmit = (data) => {
    const glAccount = data.glAccountId || "";
    pushUrlSearch(
      { startDate, endDate },
      `/admin/finance/statement/sub/${glAccount}`
    );
    totalCredit.current = 0;
    totalDebit.current = 0;
    initial.current = 0;
    setTableData([]);
    setPage(0);
    delete data["branchId"];
    data["fromDate"] = startDate;
    data["toDate"] = endDate;
    data["startDate"] = startDate;
    data["endDate"] = endDate;
    for (const key in data) {
      if (data[key] === "") {
        delete data[key];
      }
    }
    setSearch({ ...data, renderState: !search?.["renderState"] });
  };

  const emptyInoiceList = data?.listJournalEntryRecords?.data.length === 0;
  const queryInputs = {
    fromDate: search?.fromDate ?? search,
    toDate: search?.toDate ?? search,
    glAccountId: userGlAccountId,
    customer: noGlAccount === null ? false : true
  };

  const onPrint = (param) => {
    const params = searchParams(queryInputs);
    const domain = `${window.location.origin}`;

    window.open(`${domain}${param}?${params}`);
  };

  return (
    <Root>
      {noGlAccount ? (
        <Box className={classes.errorBox}>
          <RiUserUnfollowLine className={classes.errorIcon} />

          <Typography variant="h6" className={classes.marginTop}>
            {t("thereAreNoGlAccount")}
          </Typography>
        </Box>
      ) : (
        <Grid
          container
          justifyContent="center"
          className={clsx(classes.mainSearch)}
        >
          {/* *******Table******* */}
          <Grid container item className={classes.table}>
            <Toolbar variant="dense" className={classes.headerToolbar}>
              <Grid container item justifyContent="flex-start">
                <Typography variant="h6">{t("accountStatement")}</Typography>
              </Grid>
              <ExportAccountStatement
                disabled={!Boolean(data) || Boolean(emptyInoiceList) || noGlAccount}
                filters={search}
              />
              <Tooltip title="print invoices">
                <Box component="span">
                  <IconButton
                    aria-label="filter list"
                    size="large"
                    onClick={() => onPrint("/finance/statement/sub")}
                    disabled={!Boolean(data) || Boolean(emptyInoiceList) || noGlAccount}
                  >
                    <Print />
                  </IconButton>
                </Box>
              </Tooltip>
            </Toolbar>
            <Toolbar className={classes.toolbar} variant="regular">
              <Grid
                container
                item
                component={"form"}
                alignItems="center"
                justifyContent="flex-start"
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  className={classes.fieldsContainer}
                >
                  <MUIDateRangeCustom
                    value={dateRange}
                    onChangeValue={(value) => setDateRange(value)}
                  />
                </Grid>
                {glAccountListPermission && (
                  <Fragment>
                    <Grid
                      item
                      sm={6}
                      md={3}
                      xs={12}
                      className={classes.fieldsContainer}
                    >
                      <ListBranches
                        label={t("branch")}
                        control={control}
                        errors={errors}
                        name={"branchId"}
                        onChangeValue={() => {
                          setValue("glAccountId", "");
                        }}
                        skipDefaultBranch={true}
                      />
                    </Grid>

                    <Grid
                      item
                      sm={6}
                      md={3}
                      xs={12}
                      className={classes.fieldsContainer}
                    >
                      <CustomAutocomplete
                        control={control}
                        errors={errors}
                        rules={{ required: t("fieldIsRequired") }}
                        name={"glAccountId"}
                        label={t("glAccount")}
                        parseData={(data) => parseData(data)}
                        query={LIST_GL_ACCOUNTS_DROPDOWN.query}
                        variables={{
                          input: {
                            typeCode: "SUB",
                            ...(watch("branchId") && {
                              branchId: {
                                value: watch("branchId"),
                                "includeNull": true
                              },
                            }),
                          },
                        }}
                        onCompleted={(data) =>
                          handelAutocompleteDefaultValue(
                            data,
                            props?.match?.params?.id,
                            "glAccount"
                          )
                        }
                        defaultValue={autocompleteValues.glAccount}
                      />
                    </Grid>
                  </Fragment>
                )}

                <Grid
                  item
                  sm={6}
                  md={3}
                  xs={12}
                  className={classes.fieldsContainer}
                >
                  <FormButton disabled={loading}>{t("search")}</FormButton>
                </Grid>
              </Grid>
            </Toolbar>
            {search && loading ? (
              <Grid container item justifyContent="center" alignItems="center">
                <Box textAlign="center" marginTop="50px" marginBottom="50px">
                  <FullScreenLoading />
                </Box>
              </Grid>
            ) : (
              data && (
                <Fragment>
                  <TableFixedHeaderWraper
                    component={Paper}
                    className={classes.tableContainer}
                  >
                    <Table aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <FixedTableCell colSpan={5} />

                          <FixedTableCell
                            className={classes.balance}
                            colSpan={2}
                          >
                            {t("balance")}
                          </FixedTableCell>
                        </TableRow>
                        <TableRow>
                          <FixedTableCell>{t("date")}</FixedTableCell>
                          <FixedTableCell>{t("recordNumber")}</FixedTableCell>
                          <FixedTableCell>
                            {t("transactionDescription")}
                          </FixedTableCell>
                          <FixedTableCell>{t("debit")}</FixedTableCell>
                          <FixedTableCell>{t("credit")}</FixedTableCell>
                          <FixedTableCell>{t("debit")}</FixedTableCell>
                          <FixedTableCell>{t("credit")}</FixedTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow className={classes.balanceFont}>
                          <FixedTableCell>{t("startBalance")}</FixedTableCell>
                          <FixedTableCell></FixedTableCell>
                          <FixedTableCell></FixedTableCell>
                          <FixedTableCell></FixedTableCell>
                          <FixedTableCell></FixedTableCell>
                          <FixedTableCell>
                            {data?.glAccount?.startBalance >= 0
                              ? "00.00"
                              : Math.abs(data?.glAccount?.startBalance) ||
                              "00.00"}
                          </FixedTableCell>
                          <FixedTableCell>
                            {data?.glAccount?.startBalance <= 0
                              ? "00.00"
                              : Math.abs(data?.glAccount?.startBalance) ||
                              "00.00"}
                          </FixedTableCell>
                        </TableRow>

                        {/* {data?.financeStatement?.records.map((row) => { */}
                        {tableData
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((row, index) => {
                            return (
                              <TableRow key={Math.random()}>
                                <FixedTableCell component="th" scope="row">
                                  {row.entry.date}
                                </FixedTableCell>
                                <CellLink
                                  pathname={`/admin/finance/journal-entry/${row.entry.id}`}
                                >
                                  {row.entry.code}
                                </CellLink>
                                <FixedTableCell>
                                  {row.description}
                                </FixedTableCell>
                                <FixedTableCell>
                                  {row.debit.toFixed(2)}
                                </FixedTableCell>
                                <FixedTableCell>
                                  {row.credit.toFixed(2)}
                                </FixedTableCell>
                                <FixedTableCell>
                                  {row.totalDebit.toFixed(2)}
                                </FixedTableCell>
                                <FixedTableCell>
                                  {row.totalCredit.toFixed(2)}
                                </FixedTableCell>
                              </TableRow>
                            );
                          })}
                        <TableRow className={classes.balanceFont}>
                          <FixedTableCell>{t("endBalance")}</FixedTableCell>
                          <FixedTableCell></FixedTableCell>
                          <FixedTableCell></FixedTableCell>
                          <FixedTableCell>
                            {data?.sumJournalEntryRecords?.debit + currency}
                          </FixedTableCell>
                          <FixedTableCell>
                            {data?.sumJournalEntryRecords?.credit + currency}
                          </FixedTableCell>
                          <FixedTableCell>
                            {data?.glAccount?.endBalance >= 0
                              ? "00.00"
                              : Math.abs(data?.glAccount?.endBalance) ||
                              "00.00"}
                            {currency}
                          </FixedTableCell>
                          <FixedTableCell>
                            {data?.glAccount?.endBalance <= 0
                              ? "00.00"
                              : Math.abs(data?.glAccount?.endBalance) ||
                              "00.00"}
                            {currency}
                          </FixedTableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableFixedHeaderWraper>

                  <MUITablePagination
                    count={data?.listJournalEntryRecords?.paginatorInfo?.total}
                    rowsPerPage={rowsPerPage}
                    page={!data?.listJournalEntryRecords ? 0 : page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    disableLastPage
                  />
                </Fragment>
              )
            )}
          </Grid>
        </Grid>
      )}
    </Root>
  );
};
export default FinanceStatement;
